import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useTheme from '../../hooks/useTheme';
import { clearAuth, getUserData } from '../../utils/auth';
import routes from '../../utils/routes';
import Button from '../Button';
import NotificationCenter from '../NotificationCenter';
import './Header.css';

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState('overview');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Get user data from local storage
  const userData = getUserData() || { name: 'User', email: 'user@example.com' };
  
  // Handle logout
  const handleLogout = async () => {
    // Prevent multiple clicks
    if (isLoggingOut) {
      // console.log('Logout already in progress, ignoring duplicate click');
      return;
    }
    
    setIsLoggingOut(true);
    // console.log('Logout initiated');
    
    try {
      // First clear authentication
      // console.log('Clearing authentication data');
      const logoutResult = await clearAuth();
      
      // Log the result of the logout process
      if (logoutResult && logoutResult.success) {
        if (logoutResult.alreadyInProgress) {
          // console.log('Another logout process was already running');
        } else {
          // console.log('Logout completed successfully:', logoutResult.message || 'All systems cleared');
        }
      } else {
        // console.warn('Logout completed with warnings');
      }
      
      // Then navigate to login page after auth is cleared
      // console.log('Navigating to login page');
      navigate(routes.public.LOGIN, { replace: true });
    } catch (error) {
      console.error('Error during logout process:', error.message || 'Unknown error');
      // Show message in console for debugging, but don't alert the user
      console.log('Forcing navigation to login page despite errors');
      // Navigate anyway to ensure user can exit even if logout fails
      navigate(routes.public.LOGIN, { replace: true });
    } finally {
      // Reset the logging out state in case the component doesn't unmount
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 1000);
    }
  };
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userData.name) return 'U';
    return userData.name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <header className={`header ${theme}`}>
      <div className="header-logo">
        <Link to="/">Trading X</Link>
      </div>
      
      <nav className="header-nav">
        {/* Navigation items removed as requested */}
      </nav>
      
      <div className="header-actions">
        <NotificationCenter />
        
        <div className="header-user">
          <div className="header-user-avatar">
            {getUserInitials()}
          </div>
        </div>
        
        <button 
          className="theme-toggle"
          onClick={toggleTheme}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Light Mode
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              Dark Mode
            </>
          )}
        </button>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          {isLoggingOut ? 'Logging out...' : 'Logout'}
        </Button>
      </div>
    </header>
  );
};

export default Header; 